import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  or,
  limit,
  startAfter
} from 'firebase/firestore'
import { useNavigate } from 'react-router-dom'
import '../../Style/Dashboard.css'
import {
  Button,
  Stack,
  Card,
  CardHeader,
  CardActions,
  IconButton,
  Dialog,
  Tooltip,
  Box,
  Badge,
  Typography,
  Avatar,
  TextField,
  MenuItem
} from '@mui/material'
import { Comment, Visibility, ContactSupport, Forum } from '@mui/icons-material'

import { useEffect, useState } from 'react'
import { PaperComponent } from '../Helpers/Drag'
import EmailShare from '../Email/EmailShare'
import WelcomeAlert from './WelcomeAlert'
import Loading from '../Helpers/Loading'
import noData from '../../Icons/noData.jpg'
import polls from '../../Icons/polls.png'
import { openDB } from 'idb'
import DashboardButtonGroup from './ButtonGroup/DashboardButtonGroup'
import PulseAdminMenu from '../Pulse/Helpers/PulseAdminMenu'
import DocAdminMenu from '../Document/Helpers/DocAdminMenu'

const DB_NAME = 'pdf-cache-db'
const DB_VERSION = 1
const STORE_NAME = 'pdfs'

const Dashboard = ({ uid, db, displayName }) => {
  const navigate = useNavigate()
  const limitLength = 10
  const [documents, setDocuments] = useState([])
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null)
  const [next, setNext] = useState(true)

  const [pulses, setPulses] = useState([])
  const [lastPulse, setLastPulse] = useState(null)
  const [nextPulse, setNextPulse] = useState(true)
  const [loading, setLoading] = useState(true)

  const [combined, setCombined] = useState([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')

  const getPulsesQuery = lastVisible => {
    const pulseRefs = collection(db, 'sliders')
    return lastVisible
      ? query(
          pulseRefs,
          or(where('uid', '==', uid), where('admins', 'array-contains', uid)),
          orderBy('editDate', 'desc'),
          startAfter(lastVisible),
          limit(2)
        )
      : query(
          pulseRefs,
          or(where('uid', '==', uid), where('admins', 'array-contains', uid)),
          orderBy('editDate', 'desc'),
          limit(limitLength)
        )
  }

  const handleSearch = event => {
    const value = event.target.value
    setSearch(value)
  }

  async function loadPulses () {
    const q = getPulsesQuery(null)
    const querySnapshot = await getDocs(q)
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1]
    if (querySnapshot.docs.length === limitLength) {
      setLastPulse(lastVisible)
    } else {
      setNextPulse(false)
    }
    let pulseArray = []
    querySnapshot.forEach(doc => {
      let s = doc.data()
      s.id = doc.id
      pulseArray.push(s)
    })
    setPulses(pulseArray)
    setLoading(false)
  }
  async function loadNextPulses () {
    setLoading(true)
    const q = getPulsesQuery(lastPulse)
    await getDocs(q).then(querySnapshot => {
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1]
      if (querySnapshot.docs.length === limitLength) {
        setLastPulse(lastVisible)
      } else {
        setNextPulse(false)
      }
      let pulseArray = []
      querySnapshot.forEach(doc => {
        let s = doc.data()
        s.id = doc.id
        pulseArray.push(s)
      })
      setPulses([...pulses, ...pulseArray])
      setLoading(false)
    })
  }

  const getQuery = lastVisible => {
    const docRefs = collection(db, 'documents')

    return lastVisible
      ? query(
          docRefs,
          or(where('uid', '==', uid), where('admins', 'array-contains', uid)),
          orderBy('editDate', 'desc'),
          startAfter(lastVisible),
          limit(2)
        )
      : query(
          docRefs,
          or(where('uid', '==', uid), where('admins', 'array-contains', uid)),
          orderBy('editDate', 'desc'),
          limit(limitLength)
        )
  }

  async function loadDocuments () {
    const q = getQuery(null)

    const querySnapshot = await getDocs(q)
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1]
    if (querySnapshot.docs.length === limitLength) {
      setLastVisibleDoc(lastVisible)
    } else {
      setNext(false)
    }
    let docArray = []
    querySnapshot.forEach(doc => {
      let d = doc.data()
      d.id = doc.id
      docArray.push(d)
    })
    setDocuments(docArray)
    setLoading(false)
  }

  async function loadNextDocuments () {
    setLoading(true)
    const q = getQuery(lastVisibleDoc)

    await getDocs(q).then(querySnapshot => {
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1]
      if (querySnapshot.docs.length === limitLength) {
        setLastVisibleDoc(lastVisible)
      } else {
        setNext(false)
      }
      let docArray = []
      querySnapshot.forEach(doc => {
        let d = doc.data()
        d.id = doc.id
        docArray.push(d)
      })
      setDocuments([...documents, ...docArray])
      setLoading(false)
    })
  }

  function loadNext () {
    if (next) {
      loadNextDocuments()
    }
    if (nextPulse) {
      loadNextPulses()
    }
  }

  useEffect(() => {
    if (uid) {
      setLoading(true)
      loadDocuments()
      loadPulses()
    }
  }, [uid])

  useEffect(() => {
    let combinedArray = [...documents, ...pulses]
    combinedArray.sort((a, b) => {
      if (a.editDate && b.editDate) {
        return b.editDate - a.editDate
      } else if (a.editDate) {
        return -1
      } else if (b.editDate) {
        return 1
      } else {
        return 0
      }
    })
    setCombined([...combinedArray])
  }, [documents, pulses])

  const isFilterDate = (start, end, prompts) => {
    const timestamp = Date.now()
    switch (filter) {
      case 'past':
        return timestamp > end.seconds * 1000
      case 'upcoming':
        return timestamp < start.seconds * 1000
      case 'active':
        return (
          timestamp > start.seconds * 1000 && timestamp < end.seconds * 1000
        )
      case 'pulse':
        return prompts && prompts.length
      case 'doc':
        return !prompts
      case 'all':
        return true
    }
  }

  const getCombined = () => {
    return combined.filter(
      val =>
        val.fileName.toLowerCase().includes(search.toLowerCase()) &&
        isFilterDate(
          val.visibleDate.startDate,
          val.visibleDate.endDate,
          val.pulseValues
        )
    )
  }

  return (
    <Card
      raised
      sx={{
        m: { md: 2, xs: 1 },
        mb: { md: 2, xs: 2 },
        mt: { md: 2, xs: 2 },
        minHeight: '95%',
        pb: 2
      }}
    >
      <Box
        sx={{
          width: '90vw',
          margin: '10px auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '90vh'
        }}
      >
        <WelcomeAlert displayName={displayName} />
        <Stack
          direction='row'
          useFlexGap
          flexWrap='wrap'
          sx={{
            justifyContent: { xs: 'center', md: 'space-between' },
            mb: 3,
            alignItems: 'center',
            gap: { md: 4, xs: 1 },
            width: '100%'
          }}
        >
          <h1>Dashboard</h1>
          <Stack
            direction='row'
            useFlexGap
            flexWrap='wrap'
            justifyContent='center'
            spacing={1}
            height='min-content'
          >
            <TextField
              id='search'
              label='Search by File Name'
              variant='outlined'
              onChange={handleSearch}
              disabled={loading}
              value={search}
              size='small'
            />

            <TextField
              id='filter'
              select
              size='small'
              label='Filter'
              variant='outlined'
              value={filter}
              onChange={event => setFilter(event.target.value)}
            >
              <MenuItem value={'all'}>All</MenuItem>
              <MenuItem value={'active'}>Active</MenuItem>
              <MenuItem value={'upcoming'}>Upcoming</MenuItem>
              <MenuItem value={'past'}>Past</MenuItem>
              <MenuItem value={'pulse'}>Pulse</MenuItem>
              <MenuItem value={'doc'}>Document</MenuItem>
            </TextField>
            <DashboardButtonGroup navigate={navigate} />
            <Tooltip title='Upload Help'>
              <IconButton
                color='primary'
                onClick={() => navigate('/support/adminhelp')}
                height='60px'
              >
                <ContactSupport />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        {getCombined().length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              width: { md: '50%', xs: '90%' },
              margin: 'auto',
              mt: 6
            }}
          >
            <img
              src={noData}
              alt='No Documents'
              style={{ borderRadius: '4px', width: '100%' }}
            />
            <Typography variant='h3' sx={{ fontWeight: 'bold' }}>
              No Documents
            </Typography>
          </Box>
        ) : (
          <Stack
            direction='row'
            useFlexGap
            flexWrap='wrap'
            spacing={2}
            sx={{
              justifyContent: { xs: 'center', md: 'flex-start' },
              width: '100%'
            }}
          >
            {getCombined().map(val => (
              <Box
                key={val.title + '_' + val.uid + val.createDate}
                className='dashboardCard'
              >
                {val.pulseValues ? (
                  <PulseTitleView document={val} db={db} />
                ) : (
                  <DocTileView document={val} db={db} />
                )}
              </Box>
            ))}
          </Stack>
        )}
        {loading && <Loading padding='50px 0' />}
        {(next || nextPulse) && !loading && (
          <Button
            variant='contained'
            sx={{ mt: 2, maxWidth: '200px' }}
            disabled={(!next && !nextPulse) || loading}
            onClick={loadNext}
          >
            Load More
          </Button>
        )}
      </Box>
    </Card>
  )
}

const PulseTitleView = ({ document, db }) => {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)

  function getDateString (date) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
    return new Date(date.toDate()).toLocaleDateString(undefined, options)
  }

  function getComboDateString (obj) {
    return getDateString(obj.startDate) + ' - ' + getDateString(obj.endDate)
  }

  function isDraftCheck (document) {
    return typeof document.draft !== 'undefined' && document.draft === true
  }

  const openPulse = () => {
    navigate(
      '/viewPulse/' +
        (typeof document.customRoute !== 'undefined' &&
        document.customRoute.length > 0
          ? document.customRoute
          : document.id)
    )
  }

  return (
    <>
      <Badge
        badgeContent={'Pulse' + (!isDraftCheck(document) ? '' : ' (DRAFT)')}
        color={!isDraftCheck(document) ? 'secondary' : 'error'}
        className='draftBadge'
      >
        <Card
          raised
          sx={{ maxWidth: 300, p: 1, display: 'flex', flexDirection: 'column' }}
        >
          <div
            className='pointer'
            style={{ textAlign: 'center' }}
            onClick={openPulse}
          >
            <Avatar
              sx={{ width: 150, height: 150 }}
              loading='lazy'
              variant='rounded'
              className='previewFrame'
              alt='Pulse Logo'
              title={'pulse ' + document.fileName + document.uid}
              src={polls}
            />
          </div>
          <CardHeader
            sx={{ width: '100vw', maxWidth: '100%', flexWrap: 'wrap' }}
            title={
              <div className='pointer' onClick={openPulse}>
                {document.fileName}
              </div>
            }
            subheader={
              <div className='pointer' onClick={openPulse}>
                {document.displayName +
                  (document.hostName ? ` (${document.hostName})` : '')}
                <br />
                {getComboDateString(document.visibleDate)}
              </div>
            }
          />
          <CardActions sx={{ justifyContent: 'flex-end', mt: 'auto' }}>
            {!isDraftCheck(document) && (
              <>
                <Tooltip title='Responses'>
                  <Button startIcon={<Forum />} onClick={openPulse}>
                    {document.comments ? document.comments.length : '0'}
                  </Button>
                </Tooltip>

                <Tooltip title='Visiter Count'>
                  <Button startIcon={<Visibility />} onClick={openPulse}>
                    {document.visitedBy ? document.visitedBy.length : 0}
                  </Button>
                </Tooltip>
              </>
            )}

            <PulseAdminMenu
              isDraftCheck={isDraftCheck}
              db={db}
              document={document}
            />
          </CardActions>
        </Card>
      </Badge>

      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        PaperComponent={PaperComponent}
        aria-labelledby='draggable-dialog-title'
        fullWidth
      >
        <EmailShare
          setShowModal={setShowModal}
          db={db}
          id={document.id}
          viewCode={document.viewCode || null}
          fileName={document.fileName}
          customURL={document.customRoute || null}
          isPulse={true}
        />
      </Dialog>
    </>
  )
}

const DocTileView = ({ document, db }) => {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)

  function getDateString (date) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
    return new Date(date.toDate()).toLocaleDateString(undefined, options)
  }

  function getComboDateString (obj) {
    return getDateString(obj.startDate) + ' - ' + getDateString(obj.endDate)
  }

  function isDraftCheck (document) {
    return typeof document.draft !== 'undefined' && document.draft === true
  }

  const [src, setSrc] = useState(null)

  useEffect(() => {
    const fetchPdf = async () => {
      const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade (db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME)
          }
        }
      })

      try {
        const cachedFile = await db.get(STORE_NAME, document.url)
        if (cachedFile) {
          const objectURL = URL.createObjectURL(cachedFile)
          setSrc(objectURL)
        } else {
          const response = await fetch(document.url)
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`)
          }
          const blob = await response.blob()
          await db.put(STORE_NAME, blob, document.url)
          const objectURL = URL.createObjectURL(blob)
          setSrc(objectURL)
        }
      } catch (error) {
        console.error('Error fetching PDF: ', error)
      }
    }
    if (document && document.url) {
      fetchPdf()
    }

    return () => {
      if (src) {
        URL.revokeObjectURL(src)
      }
    }
  }, [document])

  const openDocument = () => {
    navigate(
      '/view/' +
        (typeof document.customRoute !== 'undefined' &&
        document.customRoute.length > 0
          ? document.customRoute
          : document.id)
    )
  }

  return (
    <>
      <Badge
        badgeContent={'Document' + (!isDraftCheck(document) ? '' : ' (DRAFT)')}
        color={!isDraftCheck(document) ? 'primary' : 'error'}
        className='draftBadge'
      >
        <Card
          raised
          sx={{ maxWidth: 300, p: 1, display: 'flex', flexDirection: 'column' }}
        >
          <div className='previewHolder'>
            <div className='previewCover' onClick={openDocument}></div>
            <iframe
              loading='lazy'
              className='previewFrame'
              title={'document ' + document.fileName + document.uid}
              src={src}
              style={{ width: '100%' }}
            />
          </div>
          <CardHeader
            title={
              <div className='pointer' onClick={openDocument}>
                {document.fileName}
              </div>
            }
            subheader={
              <div className='pointer' onClick={openDocument}>
                {document.displayName +
                  (document.hostName ? ` (${document.hostName})` : '')}
                <br />
                {getComboDateString(document.visibleDate)}
              </div>
            }
          />
          <CardActions sx={{ justifyContent: 'flex-end', mt: 'auto' }}>
            {!isDraftCheck(document) && (
              <>
                <Tooltip title='Comments'>
                  <Button startIcon={<Comment />} onClick={openDocument}>
                    {document.comments ? document.comments.length : '0'}
                  </Button>
                </Tooltip>

                <Tooltip title='Visiter Count'>
                  <Button startIcon={<Visibility />} onClick={openDocument}>
                    {document.visitedBy ? document.visitedBy.length : 0}
                  </Button>
                </Tooltip>
                <DocAdminMenu
                  document={document}
                  db={db}
                  isDraftCheck={isDraftCheck}
                />
              </>
            )}
          </CardActions>
        </Card>
      </Badge>

      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        PaperComponent={PaperComponent}
        aria-labelledby='draggable-dialog-title'
        fullWidth
      >
        <EmailShare
          setShowModal={setShowModal}
          db={db}
          id={document.id}
          viewCode={document.viewCode || null}
          fileName={document.fileName}
          customURL={document.customRoute || null}
        />
      </Dialog>
    </>
  )
}

export default Dashboard
